import React from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Paper, Typography } from '@mui/material';
import type { Client, ClientStatus } from '@/types/client';
import { shouldShowClientDataRetentionWarning } from '@/clients/details/v2/components/clientDetailsV2.utils';
import {
  formatClientRestoreDeadline,
  getClientRecoverableUntil,
  isPendingDeletionClient,
} from '@/utils/client-account-deletion';

type ClientDetailsV2RetentionBannerProps = {
  status: ClientStatus | string | undefined | null;
  client?: Pick<Client, 'status' | 'accountDeletion'> | null;
};

export const ClientDetailsV2RetentionBanner: React.FC<ClientDetailsV2RetentionBannerProps> = ({
  status,
  client,
}) => {
  const pendingDeletion = isPendingDeletionClient(client ?? { status });
  const restoreUntil = formatClientRestoreDeadline(getClientRecoverableUntil(client));

  if (pendingDeletion) {
    return (
      <Paper
        elevation={0}
        role="status"
        sx={{
          border: '1px solid #FDBA74',
          borderRadius: '12px',
          bgcolor: '#FFF7ED',
          px: 1.5,
          py: 1.25,
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <WarningAmberOutlinedIcon sx={{ color: '#D97706', fontSize: 20, flexShrink: 0, mt: 0.1 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#92400E',
              lineHeight: 1.4,
            }}
          >
            Deleted in companion app
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8125rem',
              color: '#78350F',
              lineHeight: 1.45,
              mt: 0.25,
            }}
          >
            This client deleted their account and is paused on your roster.
            {restoreUntil ? ` They can restore it until ${restoreUntil}.` : ' They have 14 days to restore it.'}
            {' '}Restore is only available in the companion app.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!shouldShowClientDataRetentionWarning(status)) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      role="status"
      sx={{
        border: '1px solid #FCD34D',
        borderRadius: '12px',
        bgcolor: '#FFFBEB',
        px: 1.5,
        py: 1.25,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <WarningAmberOutlinedIcon sx={{ color: '#D97706', fontSize: 20, flexShrink: 0, mt: 0.1 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: '#92400E',
            lineHeight: 1.4,
          }}
        >
          Data retention notice
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.8125rem',
            color: '#78350F',
            lineHeight: 1.45,
            mt: 0.25,
          }}
        >
          This client&apos;s data will be permanently deleted in 1 month unless they are converted to an
          active client.
        </Typography>
      </Box>
    </Paper>
  );
};
